import { observer } from "mobx-react-lite";
import { useCallback, useRef } from "react";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSMenuDropdownSlotName,
  MDSIconButton,
  MDSIconKind,
  MDSMenuDropdownPopover,
  MDSMenuItemVariant,
  MDSMenuItemDivider,
} from "@/mds";
import { actions } from "@/actions";
import { useAppStore } from "@/store";
import { NoteSourceArtifactInfo } from "./types";

interface FloatingAudioPreviewDropdownMenuProps {
  noteId: string;
  sourceId: string;
  artifactInfo: NoteSourceArtifactInfo | null;
  isDropdownMenuOpen: boolean;
  setIsDropdownMenuOpen: (isOpen: boolean) => void;
  isRetranscribing: boolean;
  handleRetranscribe: () => void;
}

export const FloatingAudioPreviewDropdownMenu = observer(function FloatingAudioPreviewDropdownMenu({
  artifactInfo,
  noteId,
  sourceId,
  isRetranscribing,
  handleRetranscribe,
  isDropdownMenuOpen,
  setIsDropdownMenuOpen,
}: FloatingAudioPreviewDropdownMenuProps) {
  const { store } = useAppStore();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleCopyTranscript = useCallback(async () => {
    setIsDropdownMenuOpen(false);
    if (!artifactInfo) return;

    await actions.copyNoteTranscript({ artifactInfo });
  }, [artifactInfo, setIsDropdownMenuOpen]);

  const handleDownloadAudio = useCallback(async () => {
    setIsDropdownMenuOpen(false);
    if (!artifactInfo) return;

    await actions.downloadArtifactAudio({ artifactInfo });
  }, [artifactInfo, setIsDropdownMenuOpen]);

  const handleRemoveSource = useCallback(async () => {
    await actions.deleteNoteSource({
      store: store,
      noteId: noteId,
      sourceId: sourceId,
    });
  }, [store, noteId, sourceId]);

  return (
    <>
      <MDSIconButton
        ref={triggerRef}
        iconKind={MDSIconKind.More}
        onClick={() => {
          setIsDropdownMenuOpen(!isDropdownMenuOpen);
        }}
      />
      <MDSMenuDropdownPopover
        triggerRef={triggerRef}
        isOpen={isDropdownMenuOpen}
        onOpenChange={setIsDropdownMenuOpen}
        placement="bottom end"
      >
        <MDSActionMenu slot={MDSMenuDropdownSlotName.Menu}>
          {artifactInfo && (
            <>
              <MDSActionMenuItem
                id="copy-transcript"
                label="Copy transcript"
                variant={MDSMenuItemVariant.OneLine}
                icon={MDSIconKind.Copy}
                onClick={handleCopyTranscript}
              />
              <MDSActionMenuItem
                id="download-audio"
                label="Download audio"
                variant={MDSMenuItemVariant.OneLine}
                icon={MDSIconKind.File}
                onClick={handleDownloadAudio}
              />
              <MDSMenuItemDivider />
            </>
          )}
          <MDSActionMenuItem
            id="remove-source"
            label="Remove source"
            variant={MDSMenuItemVariant.OneLine}
            icon={MDSIconKind.Trash}
            onClick={handleRemoveSource}
          />
          <MDSActionMenuItem
            id="retranscribe"
            label="Retranscribe"
            variant={MDSMenuItemVariant.OneLine}
            icon={MDSIconKind.Refresh}
            onClick={handleRetranscribe}
            isDisabled={isRetranscribing}
          />
        </MDSActionMenu>
      </MDSMenuDropdownPopover>
    </>
  );
});
